// The unfilled-placeholder highlight for a plain-text reply box (2026-10-26,
// docs/contact-view.md, "The agent draft panel"): eBay and every other
// plain-text channel, where the box is exactly what is sent.
//
// The textarea stays the box: value, caret, selection, IME, spellcheck, undo,
// resize and keys are all its own, untouched. This draws a layer over it,
// pointer-events none and aria-hidden, that copies the textarea's font,
// padding and wrapping, holds the same text in transparent ink, and paints a
// fill behind each placeholder only; it follows the textarea's size and
// scroll. In light mode the fill multiplies with what is under it (the
// email editor's #FFF1C9 on white, the text stays dark); in dark mode it is a
// translucent amber. Nothing is ever written to the value.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTheme } from '@mui/material/styles'
import {
  placeholderSegments, hasPlaceholderHighlight, mirrorText, MIRRORED_STYLE_PROPS,
  PLACEHOLDER_HIGHLIGHT_BG, PLACEHOLDER_HIGHLIGHT_BG_DARK,
} from './placeholderHighlight.js'

function sameGeometry(a, b) {
  if (!a || !b) return a === b
  if (a.left !== b.left || a.top !== b.top || a.width !== b.width || a.height !== b.height) return false
  return MIRRORED_STYLE_PROPS.every((k) => a.style[k] === b.style[k])
}

// value: the textarea's value. textareaRef / containerRef: the textarea and
// the positioned box this layer is placed in.
export default function PlaceholderOverlay({ value, textareaRef, containerRef }) {
  const theme = useTheme()
  const dark = theme.palette.mode === 'dark'
  const active = hasPlaceholderHighlight(value)
  const layerRef = useRef(null)
  const [geom, setGeom] = useState(null)

  const syncScroll = useCallback(() => {
    const ta = textareaRef.current
    const layer = layerRef.current
    if (!ta || !layer) return
    layer.scrollTop = ta.scrollTop
    layer.scrollLeft = ta.scrollLeft
  }, [textareaRef])

  const measure = useCallback(() => {
    const ta = textareaRef.current
    const box = containerRef.current
    if (!ta || !box || ta.tagName !== 'TEXTAREA') { setGeom(null); return }
    const cs = window.getComputedStyle(ta)
    const t = ta.getBoundingClientRect()
    const c = box.getBoundingClientRect()
    const style = {}
    for (const k of MIRRORED_STYLE_PROPS) style[k] = cs[k]
    const next = {
      left: t.left - c.left + ta.clientLeft,
      top: t.top - c.top + ta.clientTop,
      // clientWidth / clientHeight: the padding box, without a scrollbar.
      width: ta.clientWidth,
      height: ta.clientHeight,
      style,
    }
    setGeom((prev) => (sameGeometry(prev, next) ? prev : next))
  }, [textareaRef, containerRef])

  useLayoutEffect(() => {
    if (!active) return
    measure()
    syncScroll()
  })

  useEffect(() => {
    if (!active) return undefined
    const ta = textareaRef.current
    if (!ta) return undefined
    const onScroll = () => syncScroll()
    ta.addEventListener('scroll', onScroll)
    window.addEventListener('resize', measure)
    let ro = null
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => measure())
      ro.observe(ta)
      if (containerRef.current) ro.observe(containerRef.current)
    }
    return () => {
      ta.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', measure)
      ro?.disconnect()
    }
  }, [active, measure, syncScroll, textareaRef, containerRef])

  const text = mirrorText(value)
  const segments = useMemo(() => placeholderSegments(text), [text])
  if (!active || !geom) return null

  const fill = dark ? PLACEHOLDER_HIGHLIGHT_BG_DARK : PLACEHOLDER_HIGHLIGHT_BG
  return (
    <div
      ref={layerRef}
      aria-hidden="true"
      data-testid="reply-placeholder-layer"
      // Inline margin: 0 so a Stack's spacing never shifts it.
      style={{
        ...geom.style,
        position: 'absolute',
        left: geom.left,
        top: geom.top,
        width: geom.width,
        height: geom.height,
        margin: 0,
        border: 0,
        boxSizing: 'border-box',
        overflow: 'hidden',
        color: 'transparent',
        background: 'transparent',
        pointerEvents: 'none',
        userSelect: 'none',
      }}
    >
      {segments.map((s, i) => (s.placeholder
        ? (
          <span key={i} data-testid="reply-placeholder" style={{
            backgroundColor: fill,
            boxShadow: `0 0 0 1px ${fill}`,
            borderRadius: 3,
            mixBlendMode: dark ? 'normal' : 'multiply',
          }}>{s.text}</span>
        )
        : s.text))}
    </div>
  )
}
